import { ICellRendererParams } from '@ag-grid-community/core'
import { DownloadOutlined, DeleteOutlined } from '@ant-design/icons'
import { ContributionReportResponseOneDto } from 'modules/contribution-report-service/dtos'
import { ContributionReportJobsStatus } from 'types/contribution'
import {
  ContributionReportQueryKeys,
  useContributionReport,
} from 'modules/contribution-report-service/hooks/useContributionReport'
import React, { useState, useEffect, useRef } from 'react'
import { Button, Modal } from 'antd'
import { Stack } from '@sureco/design-system'
import { useQueryClient } from '@tanstack/react-query'

export default function ContributionReportDownloadCell({
  data,
}: ICellRendererParams<ContributionReportResponseOneDto>) {
  const queryClient = useQueryClient()
  const { getContributionReport, deleteContributionReport } =
    useContributionReport()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const prevIsDeletingRef = useRef(false)

  // Delete contribution report mutation
  const deleteContributionReportMutation = deleteContributionReport({
    mutationOptions: {
      onSuccess: () => {
        // Invalidate and refetch the reports list after successful deletion
        if (data?.clientPublicKey) {
          queryClient.invalidateQueries({
            queryKey: [
              ContributionReportQueryKeys.GET_ALL,
              data.clientPublicKey,
            ],
          })
        }
      },
    },
  })

  const isDeleting = deleteContributionReportMutation.isPending

  if (!data) {
    return null
  }

  // Fetch once if completed but reportUrl missing (ProgressCell handles in-progress polling)
  const needsReportUrl =
    !data.reportUrl && data.status === ContributionReportJobsStatus.COMPLETED

  const { data: reportData } = getContributionReport(data?.id ?? '', {
    queryOptions: {
      enabled: needsReportUrl,
      refetchInterval: false,
    } as any, // Type workaround - queryKey is set internally by the hook
  })

  const currentData = reportData ?? data

  const hasReportUrl = !!currentData?.reportUrl
  const isCompleted =
    currentData?.status === ContributionReportJobsStatus.COMPLETED

  // Disable delete button while report is being generated
  const isGenerating =
    currentData?.status === ContributionReportJobsStatus.CREATED ||
    currentData?.status === ContributionReportJobsStatus.PROCESSING ||
    currentData?.status === ContributionReportJobsStatus.AGGREGATING

  const handleDelete = () => {
    if (currentData?.id && !isDeleting) {
      deleteContributionReportMutation.mutate(currentData.id)
    }
  }

  // Close modal when deletion completes (isDeleting changes from true to false)
  useEffect(() => {
    if (prevIsDeletingRef.current && !isDeleting && isDeleteModalOpen) {
      setIsDeleteModalOpen(false)
    }
    prevIsDeletingRef.current = isDeleting
  }, [isDeleting, isDeleteModalOpen])

  return (
    <Stack
      direction="row"
      space={8}
      style={{ alignItems: 'center', height: '100%' }}
    >
      <Button
        type="link"
        icon={<DownloadOutlined />}
        href={currentData?.reportUrl}
        disabled={!hasReportUrl || !isCompleted}
        size="small"
        target="_blank"
      >
        Download
      </Button>
      <Button
        type="link"
        icon={<DeleteOutlined />}
        size="small"
        danger
        disabled={isGenerating || isDeleting}
        loading={isDeleting}
        onClick={() => setIsDeleteModalOpen(true)}
      >
        Delete
      </Button>
      <Modal
        open={isDeleteModalOpen}
        title="Delete Contribution Report"
        onOk={handleDelete}
        onCancel={() => !isDeleting && setIsDeleteModalOpen(false)}
        okText="Delete"
        okButtonProps={{ danger: true, loading: isDeleting }}
        cancelText="Cancel"
        confirmLoading={isDeleting}
        closable={!isDeleting}
        maskClosable={!isDeleting}
      >
        <p>Are you sure you want to delete this contribution report?</p>
      </Modal>
    </Stack>
  )
}
